import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from './../../Api/ApiUrls';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Card,
  CircularProgress,
  Avatar,
  Badge,
  Dialog,
  Button,
} from '@mui/material';

import FormsBottomNavbar from './../Post Module/FormsBottomNavbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';

// ── Palette ──
const TITLE_DARK = '#3B0A0A';
const BROWN = '#7A3E1D';
const CARD_BG = '#FDEFE3';
const CARD_BORDER = '#F3DCC6';
const SUBTLE = '#7A6A5A';
const GREEN = '#25A244';

const timeAgo = (dateStr) => {
  const diff = Math.max(0, Date.now() - new Date(dateStr).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins > 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

const isNew = (dateStr) => Date.now() - new Date(dateStr).getTime() < 60 * 60 * 1000;

const formatPhone = (phone) => {
  if (!phone) return 'N/A';
  const digits = String(phone).replace(/\D/g, '').slice(-10);
  if (digits.length < 10) return phone;
  return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
};

function WishlistUsersPage() {
  const { propertyId } = useParams();
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedNumber, setCopiedNumber] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);

  const handleCopyPhone = async (rawPhone) => {
    if (!rawPhone) return;
    const number = String(rawPhone).replace(/\D/g, '').slice(-10);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(number);
      } else {
        const ta = document.createElement('textarea');
        ta.value = number;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedNumber(number);
      setCopyOpen(true);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  useEffect(() => {
    const fetchWishlistUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/user-cart/`);

        // Step 1: only this property, and not the logged-in user
        const filtered = response.data.filter(
          (item) => item.property_id == propertyId && item.user_id !== userId
        );

        // Step 2: one entry per user (latest first)
        const sorted = [...filtered].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        const uniqueUsers = [...new Map(sorted.map((item) => [item.user_id, item])).values()];

        // Step 3: each user's own details
        const withDetails = await Promise.all(
          uniqueUsers.map(async (cartItem) => {
            try {
              const res = await axios.get(`${BASE_URL}/users/${cartItem.user_id}/`);
              return { ...cartItem, userDetails: res.data };
            } catch (err) {
              console.error(`Error fetching user ${cartItem.user_id}:`, err);
              return { ...cartItem, userDetails: { username: 'Unknown User', mobile_no: '' } };
            }
          })
        );

        setUsers(withDetails);
      } catch (error) {
        console.error('Error fetching wishlist users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWishlistUsers();
  }, [propertyId, userId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        background: `
          linear-gradient(145deg, rgba(232,224,208,0.95), rgba(216,204,186,0.95)),
          url('/construction4.png')
        `,
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* ===== HEADER ===== */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          background: `
            linear-gradient(145deg,rgb(22, 22, 22),rgb(15, 15, 15)),
            url('/construction5.png')
          `,
          color: 'text.primary',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{
              color: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, ml: 2, color: 'white', fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
          >
            Added propertyId {propertyId} to wishlist
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ===== MAIN CONTENT ===== */}
      <Container
        disableGutters
        sx={{
          pt: 2,
          px: { xs: 1, sm: 2 },
          mt: '64px',
          mb: '90px',
          maxWidth: '520px !important',
          width: '100%',
        }}
      >
        <Box
          sx={{
            borderRadius: '18px',
            background: '#FFFFFF',
            boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
            p: { xs: 1.2, sm: 2 },
          }}
        >
          {/* ── Card header ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1.5 }}>
            <Badge
              badgeContent={users.length}
              color="error"
              overlap="circular"
              sx={{
                '& .MuiBadge-badge': { fontWeight: 700, fontSize: '0.62rem', minWidth: 16, height: 16 },
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 40, sm: 52 },
                  height: { xs: 40, sm: 52 },
                  bgcolor: CARD_BG,
                  color: BROWN,
                }}
              >
                <FavoriteRoundedIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
              </Avatar>
            </Badge>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  color: TITLE_DARK,
                  lineHeight: 1.2,
                }}
              >
                Wishlist Users
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.66rem', sm: '0.76rem' }, color: SUBTLE, lineHeight: 1.25 }}>
                Users who added this property to their wishlist.
              </Typography>
            </Box>
          </Box>

          {/* ── List ── */}
          {users.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.4 } }}>
              {users.map((item, index) => {
                const user = item.userDetails || {};
                const name = user.username || 'Unknown User';
                const phone = formatPhone(user.mobile_no);

                return (
                  <Card
                    key={item.cart_id || index}
                    elevation={0}
                    sx={{
                      p: { xs: 1, sm: 1.4 },
                      borderRadius: '14px',
                      background: CARD_BG,
                      border: `1px solid ${CARD_BORDER}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.8,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Top row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.3 } }}>
                      <Avatar
                        sx={{
                          width: { xs: 34, sm: 44 },
                          height: { xs: 34, sm: 44 },
                          bgcolor: '#F9DCC4',
                          color: BROWN,
                          flexShrink: 0,
                        }}
                      >
                        <FavoriteRoundedIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 0.6,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.85rem', sm: '1rem' },
                              color: TITLE_DARK,
                              lineHeight: 1.2,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            Added to Wishlist
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Typography
                              sx={{
                                fontSize: { xs: '0.58rem', sm: '0.68rem' },
                                color: SUBTLE,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {timeAgo(item.created_at)}
                            </Typography>
                            {isNew(item.created_at) && (
                              <Box
                                sx={{
                                  bgcolor: '#E53935',
                                  color: '#fff',
                                  fontSize: '0.54rem',
                                  fontWeight: 800,
                                  px: 0.7,
                                  py: 0.05,
                                  borderRadius: '999px',
                                }}
                              >
                                NEW
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: { xs: '0.66rem', sm: '0.76rem' },
                            color: SUBTLE,
                            mt: 0.2,
                            lineHeight: 1.3,
                          }}
                        >
                          This property has been added to the Wishlist.
                        </Typography>
                      </Box>
                    </Box>

                    {/* User row */}
                    <Box
                      sx={{
                        p: { xs: 0.7, sm: 0.9 },
                        bgcolor: '#fff',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 1,
                        rowGap: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, flex: '1 1 auto' }}>
                        <Avatar
                          sx={{
                            width: { xs: 26, sm: 32 },
                            height: { xs: 26, sm: 32 },
                            bgcolor: '#7A4A2B',
                            fontSize: { xs: '0.72rem', sm: '0.85rem' },
                            flexShrink: 0,
                          }}
                        >
                          {name.charAt(0).toUpperCase()}
                        </Avatar>

                       <Box sx={{ minWidth: 0, flex: 1 }}>
                         <Typography
  sx={{
    fontWeight: 700,
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    color: TITLE_DARK,
    lineHeight: 1.15,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  }}
>
  {name}
</Typography>
                         
                        </Box>
                      </Box>

                      <Box
                        onClick={() => handleCopyPhone(user.mobile_no)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') handleCopyPhone(user.mobile_no);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.4,
                          flexShrink: 0,
                          pl: 1,
                          borderLeft: '1px solid #E5DACB',
                          cursor: user.mobile_no ? 'pointer' : 'default',
                          WebkitTapHighlightColor: 'transparent',
                          '&:active': { opacity: 0.6 },
                        }}
                      >
                        <WhatsAppIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: GREEN }} />
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            color: TITLE_DARK,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <FavoriteRoundedIcon sx={{ fontSize: 56, color: '#bdbdbd', mb: 1 }} />
              <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                No users have added this property to their wishlist yet
              </Typography>
            </Box>
          )}
        </Box>
      </Container>

      {/* ===== COPIED POPUP ===== */}
      <Dialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '22px',
            width: '100%',
            maxWidth: 300,
            m: 2,
            px: { xs: 2.5, sm: 3 },
            pt: 3.5,
            pb: 3,
            textAlign: 'center',
          },
        }}
      >
        <FileCopyOutlinedIcon sx={{ fontSize: 64, color: '#2E7D32', alignSelf: 'center' }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1b1b1b', mt: 1.5 }}>
          Copied Successfully
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#6b6b6b', mt: 1 }}>
          Copied: {copiedNumber}
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={() => setCopyOpen(false)}
          sx={{
            mt: 3,
            py: 1.1,
            borderRadius: '14px',
            textTransform: 'none',
            fontWeight: 800,
            fontSize: '1rem',
            bgcolor: '#2E7D32',
            boxShadow: '0 4px 10px rgba(46,125,50,0.35)',
            '&:hover': { bgcolor: '#27692a' },
          }}
        >
          OK
        </Button>
      </Dialog>

      {/* ===== BOTTOM NAV ===== */}
      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
}

export default WishlistUsersPage;